import { Box, Flex, List, ListItem, Spinner, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { BrregOrgDto } from "../../autogen/bff-api";
import { formatPostalAddress } from "./brregAddress";
import { BrregSearchResult } from "./useBrregSearch";

interface Props {
  result: BrregSearchResult;
  onSelect: (org: BrregOrgDto) => void;
}

export const BrregSuggestionList = ({ result, onSelect }: Props) => {
  const { t } = useTranslation();
  const { matches, isFetching, isUnavailable, invalidOrgNumber, mode } = result;

  const showNoHits = mode !== "idle" && !isFetching && !isUnavailable && !invalidOrgNumber && matches.length === 0;
  const hasContent = isFetching || isUnavailable || invalidOrgNumber || matches.length > 0 || showNoHits;

  if (!hasContent) return null;

  return (
    <Box
      position="absolute"
      top="full"
      left={0}
      right={0}
      mt={1}
      bg="smBackground"
      borderWidth="1px"
      borderColor="smBorder"
      borderRadius="md"
      boxShadow="md"
      zIndex={10}
      maxH="280px"
      overflowY="auto"
    >
      {invalidOrgNumber && (
        <Text p={3} fontSize="sm" color="smMuted">
          {t("The organization number must have 9 digits.")}
        </Text>
      )}
      {isUnavailable && (
        <Text p={3} fontSize="sm" color="smMuted">
          {t("The Brønnøysund registry is currently unavailable. You can register the company manually.")}
        </Text>
      )}
      {isFetching && matches.length === 0 && (
        <Flex p={3} align="center" gap={2}>
          <Spinner size="xs" />
          <Text fontSize="sm" color="smMuted">
            {t("Searching Brønnøysund")}…
          </Text>
        </Flex>
      )}
      {showNoHits && (
        <Text p={3} fontSize="sm" color="smMuted">
          {t("No companies found in Brønnøysund. You can register the company manually.")}
        </Text>
      )}
      <List>
        {matches.map((org) => (
          <ListItem
            key={org.organizationNumber}
            px={3}
            py={2}
            cursor="pointer"
            _hover={{ bg: "smBackgroundTertiary" }}
            onMouseDown={(e) => {
              e.preventDefault();
              onSelect(org);
            }}
          >
            <Text fontWeight="semibold" fontSize="sm">
              {org.name}
            </Text>
            <Text fontSize="xs" color="smMuted">
              {org.organizationNumber}
              {formatPostalAddress(org) ? ` · ${formatPostalAddress(org)}` : ""}
            </Text>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};
